'use strict';

// external imports
import React, {
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {keys} from 'ts-transformer-keys';

// shared imports
import Item from '~/shared/src/ci/Item';
import {dataExtraction} from '~/shared/src/utilities/dataExtraction';
import {MapDataSet} from '~/shared/src/utilities/mapDataSet';
import {
    LoadMapSearchDataParameters,
    LoadMapSearchDataResult
} from '~/shared/src/utilities/modules/map';
import {conditionSchema} from '~/shared/src/utilities/condition';

// prime imports
import {Card} from 'primereact/card';

// frontend imports
import Map, {
    buildMarkerGroup,
    ElusiveData as MapElusiveData,
    MarkerGroup,
    ReferenceType as MapReferenceType
} from '~/frontend/src/components/base/Map';

// action imports
import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import {setElusiveData} from '~/frontend/src/actions/elusiveData';
import startRequest from '~/frontend/src/actions/ajax/startRequest';
import endRequest from '~/frontend/src/actions/ajax/endRequest';
import loadDataFromBackend from '~/frontend/src/actions/data/loadData';


type LoadParamsCache = {
    [ queryConditionId : string ] : string;
};

export type DefaultProps = {
    rootItemFormState? : 'CLEAR' | 'DIRTY' | 'ERROR';
};
export const defaultProps : Required<DefaultProps> = {
    rootItemFormState            : 'CLEAR'
};

export type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The dataContext for the form data to access global.data with.
     */
    dataDataContext : string;

    /**
     * The dataContext for the form configuration to access global.data with.
     */
    dataContext : string;

    /**
     * The id of the root config item of the form.
     */
    rootItemId? : number;

    selectedElementId? : number;

    /**
     * Callback if a layout element is selected.
     *
     * @param id
     */
    onSelectElement? : ( id? : number ) => void;

};
export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

export type AllProps = Required<DefaultProps> & Props;


type ElusiveData = MapElusiveData & {
    dataSets? : MapDataSet[];
    markers? : MarkerGroup[];
};


export type ReferenceType = {
    dataSets? : MapDataSet[];
    markers? : MarkerGroup[];
    resetForReload : () => void;
};


const MapSearch = React.forwardRef<ReferenceType, AllProps>( ( props : AllProps, forwardedRef ) => {
    const dispatch : AppDispatch          = useDispatch(),
          {
              dataContext,
              dataDataContext,
              onSelectElement,
              rootItemId,
              rootItemFormState,
              selectedElementId,
              uid
          }                               = props,
          user                            = useSelector( ( state : StoreState ) => state.session?.user ? state.session.user : undefined ),
          elusiveData                     = useSelector( ( state : StoreState ) => state.elusiveData?.[ uid ] as ElusiveData | undefined ),
          allElusiveData                  = useSelector( ( state : StoreState ) => state.elusiveData ),
          changedDataSets                 = useRef<MapDataSet[]>( [] ),
          markers                         = useRef<MarkerGroup[]>( [] ),
          lastParams                      = useRef<any>( null ),
          loadParamsCache                 = useRef<LoadParamsCache>( {} ),
          map                             = useRef<MapReferenceType | null>( null ),
          elusiveDataCache                = useRef<ElusiveData | undefined>( undefined ),
          [ initialized, setInitialized ] = useState<boolean>( false ),
          language                        = useMemo(
              () => {
                  return user?.language ?? '';
              },
              [
                  user
              ]
          ),
          globalDataVersions              = useMemo(
              () => {
                  return JSON.stringify( allElusiveData?.[ dataContext ]?.data ?? {} );
              },
              [
                  allElusiveData?.[ dataContext ]?.data
              ]
          ),
          globalDataDataVersions          = useMemo(
              () => {
                  return JSON.stringify( allElusiveData?.[ dataDataContext ]?.data ?? {} );
              },
              [
                  allElusiveData?.[ dataDataContext ]?.data
              ]
          ),
          rootItem                        = useMemo(
              () => {
                  let retVal : Item | undefined;

                  if ( rootItemId ) {
                      retVal = global.data[ dataContext ]?.items[ rootItemId ];
                  }

                  return retVal;
              },
              [
                  dataContext,
                  rootItemId,
                  globalDataVersions
              ]
          ),
          schema                          = useMemo(
              () => {
                  let retVal : Schema = 'DATA';

                  if ( global.data[ dataDataContext ]?.schema ) {
                      retVal = global.data[ dataDataContext ].schema;
                  }

                  return retVal;
              },
              [
                  dataDataContext,
                  globalDataDataVersions
              ]
          ),
          onMapSearchClick                = ( e : React.MouseEvent<HTMLDivElement> ) => {
              lastParams.current?.onSelectElement?.( undefined );
          },
          dataSets                        = useMemo(
              () => {
                  let retVal = elusiveData?.dataSets ?? [];

                  changedDataSets.current = [];

                  if (
                      rootItem
                      && (
                          !retVal.length
                          || (
                              lastParams.current
                              && (
                                  dataContext !== lastParams.current.dataContext
                                  || rootItem !== lastParams.current.rootItem
                                  || globalDataVersions !== lastParams.current.globalDataVersions
                              )
                          )
                      )
                  ) {
                      retVal = [];

                      const newDataSets = dataExtraction.extractMapDataSetsDataStructureQueryOnly( rootItem, dataContext );

                      if ( newDataSets.length && elusiveData?.dataSets ) {
                          for ( let i = 0; i < newDataSets.length; i++ ) {
                              const oldDataSet = elusiveData?.dataSets.find( dS => dS.data.id === newDataSets[ i ].data.id && JSON.stringify( dS ) === JSON.stringify( newDataSets[ i ] ) );

                              // nothing changed
                              if ( oldDataSet ) {
                                  retVal.push( oldDataSet );
                              }
                              // changed or new
                              else {
                                  retVal.push( newDataSets[ i ] );
                                  changedDataSets.current.push( newDataSets[ i ] );
                              }
                          }
                      }
                      // all new
                      else {
                          retVal = newDataSets;
                          changedDataSets.current = newDataSets;
                      }

                      //dispatch( setElusiveData( uid, { dataSets: retVal } ) );

                      // remove groups for removed dataSets from markers (not in retVal)
                      if ( elusiveData?.markers?.length ) {
                          const newMarkers = ( [] as MarkerGroup[] ).concat( elusiveData.markers );

                          for ( let i = newMarkers.length - 1; i >= 0; i-- ) {
                              const index = retVal.findIndex( dS => dS.data.active && dS.data.id === newMarkers[ i ].dataSetId );

                              if ( index === -1 ) {
                                  newMarkers.splice( i, 1 );
                              }
                          }

                          if ( newMarkers.length !== elusiveData.markers.length ) {
                              dispatch( setElusiveData( uid, { markers: newMarkers } ) );
                              markers.current = newMarkers;
                          }
                      }
                  }

                  return retVal;
              },
              [
                  dataContext,
                  rootItem,
                  globalDataVersions
              ]
          ),
          resetForReload                  = () => {
              markers.current = [];
              lastParams.current = null;
              loadParamsCache.current = {};
              setInitialized( false );
          },
          loadData                        = async ( dataSet : MapDataSet ) => {
              if ( dataSet.query.dataStructure ) {
                  dispatch( startRequest(
                      () => {
                      },
                      uid,
                      'processing'
                  ) );

                  let conditionValid       = true,
                      dataDescriptorsValid = false;

                  // validate condition to avoid backend error
                  if ( dataSet.condition ) {
                      const conditionValidationResult = conditionSchema.validate(
                          dataSet.condition,
                          {
                              abortEarly  : false,
                              stripUnknown: true,
                              convert     : false
                          }
                      );

                      if ( conditionValidationResult.error ) {
                          conditionValid = false;
                      }
                  }

                  if ( dataSet.dataDescriptors && dataSet.rootDataDescriptor && dataSet.locationDataDescriptor ) {
                      dataDescriptorsValid = true;
                  }

                  if ( conditionValid && dataDescriptorsValid ) {
                      const dataSetDataContext                   = `${dataDataContext}_mapDataSet_${dataSet.data.id}`,
                            params : LoadMapSearchDataParameters = {
                                dataStructure  : dataSet.query.dataStructure,
                                dataDescriptors: dataSet.dataDescriptors,
                                condition      : dataSet.condition
                            },
                            stringifiedParams                    = JSON.stringify( params );

                      if ( !elusiveDataCache.current ) {
                          elusiveDataCache.current = elusiveData;
                      }

                      let response : boolean,
                          newElusiveData = { ...elusiveDataCache.current };

                      if ( !global.data[ dataSetDataContext ] || !loadParamsCache.current[ dataSet.data.id ] || loadParamsCache.current[ dataSet.data.id ] !== stringifiedParams ) {
                          loadParamsCache.current[ dataSet.data.id ] = stringifiedParams;

                          if ( global.data[ dataSetDataContext ] ) {
                              delete global.data[ dataSetDataContext ];
                          }

                          response = await dispatch(
                              loadDataFromBackend(
                                  'map/loadMapSearchData',
                                  dataSetDataContext,
                                  uid,
                                  'loadData',
                                  dataSet.query.schema ?? schema,
                                  {
                                      params,
                                      mergeMode: 'override'
                                  },
                                  undefined,
                                  ( responseData ) => {
                                      const data = responseData as LoadMapSearchDataResult;

                                      if ( data.success ) {
                                          newElusiveData = {
                                              metaData: {
                                                  ...newElusiveData?.metaData,
                                                  mapDataSetMetaData: {
                                                      ...newElusiveData?.metaData?.mapDataSetMetaData,
                                                      [ dataSet.data.key ]: {
                                                          rowData       : data.metaData.rowData,
                                                          rowDataMapping: data.metaData.rowDataMapping
                                                      }
                                                  }
                                              }
                                          };

                                          elusiveDataCache.current = newElusiveData;
                                          dispatch( setElusiveData( uid, { metaData: newElusiveData.metaData } ) );
                                      }
                                  }
                              )
                          );
                      }
                      else {
                          response = true;
                      }

                      if ( response ) {
                          const newMarkers : MarkerGroup[] = [];

                          for ( let i = 0; i < dataSets.length; i++ ) {
                              let markerGroup : MarkerGroup | undefined = undefined;

                              if ( dataSets[ i ] === dataSet ) {
                                  markerGroup = buildMarkerGroup(
                                      dataSets[ i ],
                                      i,
                                      dataSetDataContext,
                                      language,
                                      newElusiveData?.metaData?.mapDataSetMetaData?.[ dataSet.data.key ]?.rowData,
                                      newElusiveData?.metaData?.mapDataSetMetaData?.[ dataSet.data.key ]?.rowDataMapping,
                                      false
                                  );
                              }
                              else {
                                  const markerGroupIndex = markers.current.findIndex( mG => mG.dataSetId === dataSets[ i ].data.id );

                                  if ( markerGroupIndex !== -1 ) {
                                      markerGroup = buildMarkerGroup(
                                          dataSets[ i ],
                                          i,
                                          dataSetDataContext,
                                          language,
                                          newElusiveData?.metaData?.mapDataSetMetaData?.[ dataSet.data.key ]?.rowData,
                                          newElusiveData?.metaData?.mapDataSetMetaData?.[ dataSet.data.key ]?.rowDataMapping,
                                          true
                                      );
                                      markerGroup.markers = markers.current[ markerGroupIndex ].markers;
                                  }
                              }

                              if ( markerGroup ) {
                                  newMarkers.push( markerGroup );
                              }
                          }

                          dispatch( setElusiveData( uid, { markers: newMarkers } ) );
                          markers.current = newMarkers;
                      }
                  }

                  dispatch( endRequest(
                      () => {
                      },
                      uid,
                      'processing'
                  ) );
              }
          };

    markers.current = useMemo(
        () => {
            let retVal = elusiveData?.markers ?? [];

            if (
                dataSets.length
                && (
                    !retVal.length
                    || (
                        lastParams.current
                        && (
                            language !== lastParams.current.language
                        )
                    )
                )
            ) {
                retVal = [];

                for ( let i = 0; i < dataSets.length; i++ ) {
                    if ( dataSets[ i ].data.active ) {
                        retVal.push( buildMarkerGroup( dataSets[ i ], i, `${dataDataContext}_mapDataSet_${dataSets[ i ].data.id}`, language ) );
                    }
                }

                markers.current = retVal;
            }

            return retVal;
        },
        [
            elusiveData?.markers,
            language
        ]
    );

    lastParams.current = {
        dataContext,
        dataDataContext,
        globalDataVersions,
        language,
        onSelectElement,
        rootItemId,
        selectedElementId,
        uid
    };

    // expose via reference
    useImperativeHandle(
        forwardedRef,
        () => ( {
            dataSets,
            markers: markers.current,
            resetForReload
        } )
    );

    // load data for changed dataSets
    useEffect(
        () => {
            if ( changedDataSets.current.length ) {
                const doFnc = async () => {
                    elusiveDataCache.current = elusiveData;
                    for ( const dataSet of changedDataSets.current ) {
                        if ( dataSet.data.active ) {
                            await loadData( dataSet );
                        }
                    }
                    elusiveDataCache.current = undefined;

                    changedDataSets.current = [];

                    if ( !initialized ) {
                        setInitialized( true );
                        map.current?.fitBounds();
                    }
                };

                void doFnc();
            }
        },
        [
            changedDataSets.current
        ]
    );

    // update dataSets in elusiveData
    useEffect(
        () => {
            dispatch( setElusiveData( uid, { dataSets } ) );
        },
        [
            dataSets
        ]
    );

    // update markers in elusiveData
    useEffect(
        () => {
            dispatch( setElusiveData( uid, { markers: markers.current } ) );
        },
        [
            markers.current
        ]
    );

    return useMemo(
        () => {
            const classNames = [
                'yn-form-card',
                'yn-view-card',
                'yn-view-card-standalone',
                'yn-view-card-standalone-view'
            ];

            if ( rootItemFormState === 'ERROR' ) {
                classNames.push( 'yn-view-card-border-error' );
            }
            else if ( rootItemFormState === 'DIRTY' ) {
                classNames.push( 'yn-view-card-border-dirty' );
            }
            else if ( selectedElementId === rootItemId ) {
                classNames.push( 'yn-view-card-border-selected' );
            }

            /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
            return <div
                id={`${uid}_view`}
                key={`${uid}_view`}
                className={classNames.join( ' ' )}
                onClick={onMapSearchClick}
            >
                <Card
                    id={`${uid}_card`}
                    key={`${uid}_card`}
                    style={{ flex: '1' }}
                >
                    <div
                        className="yn-view-wrapper yn-view-wrapper-map yn-view-wrapper-card"
                    >
                        <div
                            id={`${uid}_border`}
                            key={`${uid}_border`}
                            className="yn-view-wrapper-border"
                        >
                            {/* @ts-expect-error forwarded components default props bug*/}
                            <Map
                                uid={uid}

                                ref={map}

                                schema={schema}
                                dataContext={dataDataContext}
                                viewKey="MapSearch"
                                externalMarkers={markers.current}
                            />
                        </div>
                    </div>
                </Card>
            </div>;
        },
        [
            dataDataContext,
            globalDataDataVersions,
            markers.current,
            rootItemFormState,
            rootItemId,
            schema,
            selectedElementId,
            uid
        ]
    );
} );

MapSearch.defaultProps = defaultProps;
MapSearch.displayName = 'MapSearch';

export default MapSearch;
